import React from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik } from "formik";
import * as Yup from "yup";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

const validationSchema = Yup.object({
  Title: Yup.string().required("Title is required"),
  Issue: Yup.string().required("Issue description is required"),
  Status: Yup.string().required("Status is required"),
});

const categories = [
  "Sale",
  "Suggestion",
  "Information",
  "Billing & Payment",
  "Service & Request",
  "Complaint & Escalation",
  "Other",
];

const statuses = [
  "Open",
  "Pending",
  "On-Hold",
  "Resolved",
  "Waiting On Customer",
  "Reopen",
  "Waiting on agent",
];

const assignees = ["Agent", "Client"];

const CreateTicket = ({ open, setOpen, getAllTicket }) => {
  const agentId = localStorage.getItem("agentId") || "";
  const userId = localStorage.getItem("userId") || "";
  const searchNo = localStorage.getItem("searchNo") || "";
  const didNumber = localStorage.getItem("didNumber") || "";
  const businessName = localStorage.getItem("businessName");

  const initialValues = {
    DIDnumber: didNumber,
    Mobile: searchNo,
    Email: "",
    Name: "",
    Title: "",
    Issue: "",
    AgentID: agentId,
    UserID: userId,
    Category: "",
    Status: "",
    Assignee: "",
    Pincode: "",
    Address: "",
    businessName: businessName,
  };

  const saveTicket = async (data) => {
    try {
      await axiosInstance.post(`${apiEndPointsConfig.SaveTicket}`, data);
      setOpen(false);
      getAllTicket();
    } catch (error) {
      console.error("Failed to save ticket:", error);
    }
  };

  return (
    <>
      <Box>
        <Button
          sx={{
            textTransform: "capitalize",
            bgcolor: "#4F46E5",
            whiteSpace: "nowrap",
            p: "10px 30px",
            borderRadius: "12px",
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
            "&:hover": {
              bgcolor: "#4338CA",
            }
          }}
          variant="contained"
          onClick={() => setOpen(true)}
        >
          Create Ticket
        </Button>
      </Box>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            padding: "8px",
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#111827">Create Support Ticket</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ border: 'none', px: 3 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={saveTicket}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              handleSubmit,
            }) => (
              <form id="create-ticket-form" onSubmit={handleSubmit}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {[
                    { name: "DIDnumber", placeholder: "DID Number" },
                    { name: "Mobile", placeholder: "Mobile" },
                    { name: "Email", placeholder: "Email" },
                    { name: "Name", placeholder: "Name" },
                    { name: "Title", placeholder: "Title" },
                    { name: "Issue", placeholder: "Issue", multiline: true },
                    { name: "Pincode", placeholder: "Pincode" },
                    {
                      name: "Address",
                      placeholder: "Address",
                      multiline: true,
                    },
                  ].map((field) => (
                    <Grid item xs={12} key={field.name}>
                      <TextField
                        name={field.name}
                        placeholder={field.placeholder}
                        value={values[field.name]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline={field.multiline || false}
                        rows={field.multiline ? 3 : 1}
                        error={
                          (field.name === "Title" || field.name === "Issue") &&
                          touched[field.name] && 
                          Boolean(errors[field.name])
                        }
                        helperText={
                          (field.name === "Title" || field.name === "Issue") &&
                          touched[field.name] && 
                          errors[field.name]
                        }
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                backgroundColor: "#F9FAFB"
                            }
                        }}
                      />
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Autocomplete
                      options={categories}
                      value={values.Category}
                      onChange={(e, value) =>
                        setFieldValue("Category", value || "")
                      }
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="Category"
                          placeholder="Select Category"
                          fullWidth
                          sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: "#F9FAFB"
                                }
                            }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={statuses}
                      value={values.Status}
                      onChange={(e, value) =>
                        setFieldValue("Status", value || "")
                      }
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="Status"
                          placeholder="Select Status"
                          fullWidth
                          error={touched.Status && Boolean(errors.Status)}
                          helperText={touched.Status && errors.Status}
                          sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: "#F9FAFB"
                                }
                            }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Autocomplete
                      options={assignees}
                      value={values.Assignee}
                      onChange={(e, value) =>
                        setFieldValue("Assignee", value || "")
                      }
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="Assignee"
                          placeholder="Assignee"
                          fullWidth
                          sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: "#F9FAFB"
                                }
                            }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #F3F4F6' }}>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{ 
              borderRadius: "12px",
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              color: '#6B7280',
              borderColor: '#E5E7EB',
              "&:hover": { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
            }}
          >
            Cancel
          </Button>
          <Button 
            form="create-ticket-form"
            type="submit"
            variant="contained"
            sx={{ 
              borderRadius: "12px",
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4F46E5',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
              "&:hover": { bgcolor: '#4338CA' }
            }}
          >
            Create Ticket
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateTicket;
